import IRoute, { IRouteInit } from 'app/constants/Routes/IRoute';
import { isSafelyAppendablePathname } from 'app/utils/routes';
import { getBasePath } from 'app/services/basePath';
import { Key, pathToRegexp } from 'path-to-regexp';

export interface IStaticRoute extends IRoute {
  getPathname(): string;
}

/**
 * Handles routes that are not parameterized.
 */
class StaticRoute implements IStaticRoute {
  public readonly syncable: boolean;

  private matcherInternal: [string, RegExp];
  private readonly route: string;

  public constructor({ route, syncable }: IRouteInit) {
    this.route = route;
    this.syncable = syncable;

    if (
      // Allow an exception for the root route path.
      route !== '/' &&
      !isSafelyAppendablePathname(this.route)
    ) {
      throw new Error(`${this.route} is not a valid pathname.`);
    }

    const keyDetails: Key[] = [];
    this.matcherInternal = [this.route, pathToRegexp(this.route, keyDetails)];

    // Throw an error if there are any route keys present.
    if (keyDetails.length > 0) {
      throw new Error(`${route} is not a static route.`);
    }
  }

  private get matcher() {
    if (this.prefixedRoute !== this.matcherInternal[0]) {
      const keyDetails: Key[] = [];
      this.matcherInternal = [this.prefixedRoute, pathToRegexp(this.prefixedRoute, keyDetails)];
      if (keyDetails.length > 0) {
        throw new Error(`${this.prefixedRoute} is not a static route.`);
      }
    }
    return this.matcherInternal[1];
  }

  private get prefixedRoute() {
    return `${getBasePath()}${this.route}`;
  }

  public decode(pathname: string) {
    if (!this.match(pathname)) {
      throw new Error('Route does not match pathname.');
    }

    return {};
  }

  public match(pathname: string): boolean {
    return this.matcher.exec(pathname) !== null;
  }

  public getPathname(): string {
    return this.prefixedRoute;
  }

  public get id(): string {
    return this.route;
  }
}

export default StaticRoute;
